<template>
    <v-row class="card" align="center">
        <v-col v-if="title" cols="12" :md="searchable ? 8 : 12" class="subtitle-text">
            {{ title }}
        </v-col>
        <v-col cols="12" md="4" v-if="searchable">
            <kimsa-text-field
                @change="search = $event"
                :value="search"
                placeholder="Buscar..."
                filled color="grey" dense outlined
            />
        </v-col>
        <v-col cols="12">
            <v-data-table
                :headers="headers"
                :items="items"
                :loading="loading"
                :items-per-page="itemsPerPage"
                @page-count="pageCount = $event"
                :page.sync="page"
                :mobile-breakpoint="0"
                :search="search"
                hide-default-header
                hide-default-footer
            >
                <!-- HEADER -->
                <template v-slot:header="{ props: { headers } }">
                    <thead>
                    <tr class="header-background-table">
                        <th v-for="h in headers" :key="h.value" class="header-text-table">
                            {{ h.text }}
                        </th>
                    </tr>
                    </thead>
                </template>

                <!-- BODY -->
                <template v-slot:body="{ items, headers }">
                    <!-- LOADING -->
                    <tr v-if="loading || items.length === 0">
                        <td :colspan="headers.length" class="text-center body-text-table py-5">
                            {{ loading ? 'Cargando...' : 'Sin datos disponibles' }}
                        </td>
                    </tr>
                    <!-- ITEMS -->
                    <tbody v-else>
                    <tr v-for="item in items" :key="item.id"
                        :class="[{'has-background-color': !!item.backgroundColor}]">
                        <td v-for="h in headers" :key="h.value" class="body-text-table"
                            :class="[{'text-truncate': !!h.truncate}]"
                            :style="`background-color: ${item.backgroundColor}`">
                            <template v-if="h.value === 'actions' && h.buttons.length > 0">
                                <v-btn
                                    v-for="button in h.buttons"
                                    :key="button.value" small
                                    text class="normal-btn body-text-table mr-1"
                                    @click="onCLickButton(item, button)"
                                >
                                    <v-icon v-if="button.icon || button.type" color="accent" size="20">
                                        {{ buttonIcon(button, item) }}
                                    </v-icon>
                                    {{ button.text }}
                                </v-btn>
                            </template>

                            <div v-else-if="h.type === 'link'">
                                <a :href="`https://${item[h.value]}`" target="_blank">
                                    {{ item[h.value] }}
                                    <v-icon color="primary" size="12">mdi-open-in-new</v-icon>
                                </a>
                            </div>
                            <div v-else-if="h.type === 'img'">
                                <v-avatar color="grey" size="35">
                                    <v-img :src="item[h.value]"/>
                                </v-avatar>
                            </div>
                            <div v-else>
                                {{ getText(item[h.value], h.type) }}
                                <v-avatar v-if="h.type === 'status'" :color="item[h.value] ? '#FC5757' : '#CCFF99'"
                                          size="20">
                                    <v-icon :color="item[h.value] ? '#FFFFFF' : '#333333'" x-small>
                                        {{ item[h.value] ? '$close' : '$check' }}
                                    </v-icon>
                                </v-avatar>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </template>

                <!-- FOOTER -->
                <template v-slot:footer v-if="pageCount > 0 && !hideFooter">
                    <div style="height: 75px;">
                        <div class="float-right">
                            <v-pagination
                                v-model="page"
                                class="mt-4 mr-4"
                                :length="pageCount"
                                :total-visible="totalVisible"
                            ></v-pagination>
                        </div>
                    </div>
                </template>
            </v-data-table>
        </v-col>
    </v-row>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";

export default {
    name: "KimsaDataTable",
    components: {KimsaTextField},
    props: {
        headers: {
            type: Array,
            default: () => [],
        },
        items: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        itemsPerPage: {
            type: [Number, String],
            default: 10,
        },
        totalVisible: {
            type: [Number, String],
            default: 5,
        },
        hideFooter: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '',
        },
        outerSearch: {
            type: String,
            default: '',
        }
    },
    data() {
        return {
            page: 1,
            pageCount: 0,
            search: '',
        }
    },
    methods: {
        onCLickButton(item, button) {
            const vm = this
            vm.$emit(`click:${button.value}`, item.id)
        },
        getText(value, type) {
            switch (type) {
                case 'boolean':
                    return value ? 'Si' : 'No'
                case 'money':
                    return this.$options.filters.money(value) || 0
                case 'rut':
                    return `${this.$options.filters.rutificador(value)}`
                case 'discount':
                    return `-${value}%`
                case 'phone':
                    return `${this.phoneCode}${value}`
                case 'status':
                    return value ? this.$t('common.inactive') : this.$t('common.active')
                default:
                    return value
            }
        },
        buttonIcon(button, item) {
            if (button.type === 'hide') return item.hide ? 'mdi-eye-off' : 'mdi-eye'
            return button.icon
        }
    },
    watch: {
        outerSearch(val) {
            this.search = val
        }
    }
}
</script>

<style scoped>

.header-text-table {
    border-bottom-color: #99CC00 !important;
    border-bottom-width: 2px !important;
    font-weight: 400 !important;
    font-size: 12px !important;
    line-height: 14px !important;
    color: #1F384C !important;
}

.body-text-table {
    border-bottom: none !important;
    font-weight: 500 !important;
    font-size: 14px !important;
    line-height: 18px !important;
    color: #464E5F !important;
}

::v-deep.v-pagination__item {
    box-shadow: none;
}

::v-deep.v-pagination__navigation {
    box-shadow: none;
}

::v-deep.v-pagination__navigation .v-icon {
    color: var(--v-primary-base) !important;
}

::v-deep.theme--light .v-pagination .v-pagination__item--active {
    color: black;
    font-weight: bold;
}

::v-deep .v-input__slot::before {
    border-style: none !important;
}

::v-deep.v-input__slot {
    border-radius: 10px !important;
    background-color: #F6F8FA !important;
}

.has-background-color:hover {
    filter: brightness(95%);
}

</style>